import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Row, Table } from 'react-bootstrap';
import { FaArrowLeft, FaCheckCircle, FaClock, FaEye, FaHourglassHalf, FaUser } from 'react-icons/fa';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import QuestionsList from '../../Components/ChecklistComponents/QuestionsList';

const BASE_URL = process.env.REACT_APP_API_URL;

// CircularProgress component (same as AuditSections)
const CircularProgress = ({ percentage, size = 80, showLabel = true }) => {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  // Get color based on percentage
  const getProgressColor = (percent) => {
    if (percent >= 100) return '#198754'; // success/green
    if (percent >= 75) return '#0d6efd';  // primary/blue
    if (percent >= 50) return '#ffc107';  // warning/yellow
    if (percent >= 25) return '#fd7e14';  // orange
    return '#dc3545';                      // danger/red
  };

  const progressColor = getProgressColor(percentage);

  return (
    <div className="circular-progress" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          className="progress-bg"
          stroke="#e9ecef"
          strokeWidth={strokeWidth}
          fill="none"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className="progress-bar"
          stroke={progressColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          fill="none"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        {showLabel && (
          <text
            x="50%"
            y="50%"
            dy=".1em"
            textAnchor="middle"
            dominantBaseline="middle"
            className="progress-text-large"
            style={{ fill: progressColor }}
          >
            {percentage}%
          </text>
        )}
      </svg>
    </div>
  );
};

function IndividualAuditOverview() {
  const [allChecklists, setAllChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    pending: 0,
    inProgress: 0,
    completed: 0,
    total: 0
  });
  const [activeTab, setActiveTab] = useState('pending');
  
  // New state for audit details view (same as AuditSections)
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [sectionNames, setSectionNames] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [subsections, setSubsections] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [answers, setAnswers] = useState({});
  const [sectionProgress, setSectionProgress] = useState(null);
  const [sectionsProgress, setSectionsProgress] = useState({});
  const [subsectionProgress, setSubsectionProgress] = useState({});
  const [currentLevel, setCurrentLevel] = useState('section');
  const [showNext, setShowNext] = useState(false);
  const [isSectionLoading, setIsSectionLoading] = useState(false);
  const [sectionData, setSectionData] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Set initial tab based on URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const statusParam = searchParams.get('status');
    
    if (statusParam && ['pending', 'inProgress', 'completed'].includes(statusParam)) {
      setActiveTab(statusParam);
    } else {
      setActiveTab('pending');
    }
  }, [location.search]);

  // Update URL when tab changes
  const handleTabSelect = (selectedTab) => {
    setActiveTab(selectedTab);
    
    const searchParams = new URLSearchParams(location.search);
    if (selectedTab === 'pending') {
      searchParams.delete('status');
    } else {
      searchParams.set('status', selectedTab);
    }
    
    const newUrl = `${location.pathname}${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    navigate(newUrl, { replace: true });
  };

  // Get status icon based on the status
  const getStatusIcon = (status) => {
    switch(status?.toLowerCase()) {
      case 'pending': return <FaClock className="me-1" size={14} />;
      case 'in progress': return <FaHourglassHalf className="me-1" size={14} />;
      case 'completed': return <FaCheckCircle className="me-1" size={14} />;
      default: return <FaClock className="me-1" size={14} />;
    }
  };

  // Get status color based on the status
  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'pending': return 'warning';
      case 'in progress': return 'primary';
      case 'completed': return 'success';
      default: return 'warning';
    }
  };

  // Fetch all checklists using the same endpoint pattern as AuditSections
  useEffect(() => {
    const fetchAllChecklists = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        
        if (!userData || !userData.BranchCode) {
          throw new Error('Admin data not found');
        }

        // Use the same endpoint structure as AuditSections
        const response = await fetch(`${BASE_URL}/get-all-assigned-checklists/${userData.BranchCode}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch checklists');
        }

        const data = await response.json();
        const allAssignedChecklists = data.checklists || [];

        // Process checklists and add progress data
        let statusStats = { pending: 0, inProgress: 0, completed: 0, total: 0 };
        
        const checklistsWithProgress = await Promise.all(
          allAssignedChecklists.map(async (checklist) => {
            try {
              // Normalize status
              let normalizedStatus = 'Pending';
              
              if (checklist.status) {
                const status = checklist.status.toLowerCase().trim();
                if (status === 'pending' || status === 'not started') {
                  normalizedStatus = 'Pending';
                } else if (status === 'in progress' || status === 'inprogress' || status === 'started') {
                  normalizedStatus = 'In Progress';
                } else if (status === 'completed' || status === 'finished' || status === 'done') {
                  normalizedStatus = 'Completed';
                }
              }

              // Fetch progress data using the same endpoint as AuditSections
              const progressResponse = await fetch(
                `${BASE_URL}/get-checklist-progress-status/${checklist.checklistId}/${userData.BranchCode}/${checklist.branchId}`
              );
              
              let progressData = {
                progressPercentage: 0,
                totalQuestions: 0,
                answeredQuestions: 0,
                remainingQuestions: 0
              };

              if (progressResponse.ok) {
                const progress = await progressResponse.json();
                progressData = {
                  progressPercentage: progress.progressPercentage || 0,
                  totalQuestions: progress.totalQuestions || 0,
                  answeredQuestions: progress.answeredQuestions || 0,
                  remainingQuestions: progress.remainingQuestions || 0
                };

                // Auto-correct status based on progress
                if (progressData.progressPercentage === 100 && normalizedStatus !== 'Completed') {
                  normalizedStatus = 'Completed';
                } else if (progressData.progressPercentage > 0 && progressData.progressPercentage < 100 && normalizedStatus === 'Pending') {
                  normalizedStatus = 'In Progress';
                }
              }

              // Count for statistics
              if (normalizedStatus === 'Pending') statusStats.pending++;
              else if (normalizedStatus === 'In Progress') statusStats.inProgress++;
              else if (normalizedStatus === 'Completed') statusStats.completed++;

              return {
                ...checklist,
                status: normalizedStatus,
                ...progressData
              };
              
            } catch (err) {
              console.error(`Error processing checklist ${checklist.checklistId}:`, err);
              
              statusStats.pending++;
              
              return {
                ...checklist,
                status: 'Pending',
                progressPercentage: 0,
                totalQuestions: 0,
                answeredQuestions: 0,
                remainingQuestions: 0
              };
            }
          })
        );

        statusStats.total = checklistsWithProgress.length;
        setStats(statusStats);
        setAllChecklists(checklistsWithProgress);
        
      } catch (err) {
        console.error('Error fetching checklists:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllChecklists();
  }, []);

  // Functions for audit details view (same logic as AuditSections)
  const handleAuditClick = async (checklist) => {
    try {
      setSelectedAudit(checklist);
      setIsSectionLoading(true);
      
      const userData = JSON.parse(localStorage.getItem('user'));
      if (!userData || !userData.BranchCode) {
        throw new Error('User data not found');
      }
      
      // Use the same endpoint as AuditSections
      const url = `${BASE_URL}/get-checklist-sections/${checklist.checklistId}/${userData.BranchCode}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch sections: ${response.status}`);
      }

      const data = await response.json();
      if (!data.sections) {
        throw new Error('No sections data in response');
      }

      setSectionNames(data.sections);
      await loadSavedAnswers(checklist);
      await fetchAllSectionsProgress(checklist, data.sections);
      
    } catch (err) {
      console.error('Error fetching audit details:', err);
      setError(err.message);
    } finally {
      setIsSectionLoading(false);
    }
  };

  // Load saved answers (same as AuditSections)
  const loadSavedAnswers = async (checklist, sectionName = null) => {
    try {
      const response = await fetch(
        `${BASE_URL}/get-answers/${checklist.checklistId}/${checklist.id}/${checklist.branchId}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch saved answers');
      }
      
      const data = await response.json();
      
      const relevantAnswers = sectionName 
        ? Object.fromEntries(
            Object.entries(data.data).filter(([_, answer]) => 
              answer.section === sectionName
            )
          )
        : data.data;

      setAnswers(prev => ({
        ...prev,
        ...relevantAnswers
      }));
      
    } catch (error) {
      console.error('Error loading saved answers:', error);
    }
  };

  // Fetch all sections progress (same as AuditSections)
  const fetchAllSectionsProgress = async (checklist, sections) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const progressMap = {};
      const subsectionProgressMap = {}; // Add separate map for subsections

      const processSection = async (path) => {
        const pathString = path.join('|');
        
        const progressResponse = await fetch(
          `${BASE_URL}/get-section-progress/${checklist.checklistId}/${userData.BranchCode}/${checklist.auditorId}/${checklist.branchId}/${pathString}`
        );
        
        if (!progressResponse.ok) throw new Error('Failed to fetch progress');
        const progressData = await progressResponse.json();
        progressMap[pathString] = progressData;

        // Also add to subsection progress map for nested items
        if (path.length > 1) {
          subsectionProgressMap[pathString] = progressData;
        }

        const detailsResponse = await fetch(
          `${BASE_URL}/get-nested-section-details/${checklist.checklistId}/${userData.BranchCode}/${pathString}`
        );
        
        if (!detailsResponse.ok) throw new Error('Failed to fetch section details');
        const sectionData = await detailsResponse.json();

        if (sectionData.subsections?.length > 0) {
          for (const subsection of sectionData.subsections) {
            await processSection([...path, subsection.name]);
          }
        }
      };

      for (const section of sections) {
        await processSection([section]);
      }

      setSectionsProgress(progressMap);
      setSubsectionProgress(subsectionProgressMap); // Set subsection progress separately
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  // Add this new function specifically for fetching subsection progress when navigating
  const fetchSubsectionProgress = async (subsectionPath) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const pathString = Array.isArray(subsectionPath) 
        ? subsectionPath.join('|')
        : subsectionPath;

      const progressResponse = await fetch(
        `${BASE_URL}/get-section-progress/${selectedAudit.checklistId}/${userData.BranchCode}/${selectedAudit.auditorId}/${selectedAudit.branchId}/${pathString}`
      );

      if (!progressResponse.ok) {
        throw new Error(`Failed to fetch progress for path: ${pathString}`);
      }

      const progressData = await progressResponse.json();
      
      // Update subsection progress state
      setSubsectionProgress(prev => ({
        ...prev,
        [pathString]: progressData
      }));

      // Then check for nested subsections
      const detailsResponse = await fetch(
        `${BASE_URL}/get-nested-section-details/${selectedAudit.checklistId}/${userData.BranchCode}/${pathString}`
      );

      if (detailsResponse.ok) {
        const details = await detailsResponse.json();
        
        if (details.subsections?.length > 0) {
          await Promise.all(details.subsections.map(async (nestedSubsection) => {
            const nestedPath = Array.isArray(subsectionPath)
              ? [...subsectionPath, nestedSubsection.name]
              : `${pathString}|${nestedSubsection.name}`;
              
            try {
              const nestedProgressResponse = await fetch(
                `${BASE_URL}/get-section-progress/${selectedAudit.checklistId}/${userData.BranchCode}/${selectedAudit.auditorId}/${selectedAudit.branchId}/${Array.isArray(nestedPath) ? nestedPath.join('|') : nestedPath}`
              );

              if (nestedProgressResponse.ok) {
                const nestedProgressData = await nestedProgressResponse.json();
                setSubsectionProgress(prev => ({
                  ...prev,
                  [Array.isArray(nestedPath) ? nestedPath.join('|') : nestedPath]: nestedProgressData
                }));
              }
            } catch (nestedError) {
              console.error(`Error fetching nested progress for ${nestedSubsection.name}:`, nestedError);
            }
          }));
        }
      }

      return progressData;
    } catch (error) {
      console.error('Error fetching subsection progress:', error);
      return null;
    }
  };

  // Update the handleItemClick function to properly fetch subsection progress
  const handleItemClick = async (itemName, itemType, parentPath = []) => {
    setIsSectionLoading(true);
    const newPath = [...parentPath, itemName];
    setCurrentPath(newPath);
    setShowNext(false);
    
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const pathParam = newPath.join('|');

      const response = await fetch(
        `${BASE_URL}/get-nested-section-details/${selectedAudit.checklistId}/${userData.BranchCode}/${pathParam}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch details');
      }

      const data = await response.json();
      setSectionData(data);
      
      const nextLevel = newPath.length === 1 ? 'subsection' : 
                       newPath.length === 2 ? 'subsubsection' : 
                       'final';
      setCurrentLevel(nextLevel);

      if (data.type === 'questions' || data.type === 'both') {
        setQuestions(data.type === 'both' ? data.questions : data.data);
        if (data.type === 'both' && data.subsections?.length > 0) {
          setShowNext(true);
          setSubsections(data.subsections);
          
          // Fetch progress for each subsection when they're displayed
          for (const subsection of data.subsections) {
            const subsectionPath = [...newPath, subsection.name];
            await fetchSubsectionProgress(subsectionPath);
          }
        } else {
          setShowNext(false);
          setSubsections([]);
        }
      } else if (data.type === 'subsections') {
        setQuestions([]);
        setSubsections(data.data);
        setShowNext(false);
        
        // Fetch progress for each subsection when they're displayed
        for (const subsection of data.data) {
          const subsectionPath = [...newPath, subsection.name];
          await fetchSubsectionProgress(subsectionPath);
        }
      }
      
      await loadSavedAnswers(selectedAudit, newPath[0]);
      await fetchSectionProgress(newPath);
    } catch (error) {
      console.error('Error:', error);
      setError(error.message);
    } finally {
      setIsSectionLoading(false);
    }
  };

  // Update the section click handler to also fetch subsection progress
  const handleSectionClick = async (sectionName, index) => {
    setSelectedSection(sectionName);
    setIsSectionLoading(true);
    setCurrentPath([sectionName]);
    setShowNext(false);
    setCurrentLevel('section');

    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      
      const response = await fetch(
        `${BASE_URL}/get-section-details/${selectedAudit.checklistId}/${userData.BranchCode}/${sectionName}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch section details');
      }

      const data = await response.json();
      setSectionData(data);
      
      if (data.type === 'questions' || data.type === 'both') {
        setQuestions(data.type === 'both' ? data.questions : data.data);
        setSubsections(data.type === 'both' ? data.subsections : []);
        setShowNext(data.type === 'both');
        
        // If there are subsections, fetch their progress
        if (data.type === 'both' && data.subsections?.length > 0) {
          for (const subsection of data.subsections) {
            const subsectionPath = [sectionName, subsection.name];
            await fetchSubsectionProgress(subsectionPath);
          }
        }
      } else if (data.type === 'subsections') {
        setQuestions([]);
        setSubsections(data.data);
        
        // Fetch progress for each subsection
        for (const subsection of data.data) {
          const subsectionPath = [sectionName, subsection.name];
          await fetchSubsectionProgress(subsectionPath);
        }
      }

      await loadSavedAnswers(selectedAudit, sectionName);
      await fetchSectionProgress([sectionName]);
    } catch (error) {
      console.error('Error:', error);
      setError(error.message);
    } finally {
      setIsSectionLoading(false);
    }
  };

  // Fetch section progress (same as AuditSections)
  const fetchSectionProgress = async (path = []) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const pathParam = path.length > 0 ? path.join('|') : '';
      const response = await fetch(
        `${BASE_URL}/get-section-progress/${selectedAudit.checklistId}/${userData.BranchCode}/${selectedAudit.auditorId}/${selectedAudit.branchId}${pathParam ? `/${pathParam}` : ''}`
      );

      if (!response.ok) throw new Error('Failed to fetch progress');
      
      const data = await response.json();
      setSectionProgress(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Add useEffect to fetch subsection progress when subsections change
  useEffect(() => {
    const fetchProgress = async () => {
      if (subsections.length > 0 && currentPath.length > 0 && selectedAudit) {
        try {
          // Fetch progress for all subsections in parallel
          await Promise.all(subsections.map(async (subsection) => {
            const subsectionPath = [...currentPath, subsection.name];
            await fetchSubsectionProgress(subsectionPath);
          }));
        } catch (error) {
          console.error('Error fetching subsection progress:', error);
        }
      }
    };

    fetchProgress();
  }, [subsections, currentPath, selectedAudit]);

  // Navigation functions (same as AuditSections)
  const navigateToPath = (index) => {
    const newPath = currentPath.slice(0, index + 1);
    handleItemClick(newPath[newPath.length - 1], 'section', newPath.slice(0, -1));
  };

  const resetNavigation = () => {
    setSelectedSection(null);
    setSubsections([]);
    setQuestions([]);
    setCurrentPath([]);
  };

  const backToAuditList = () => {
    setSelectedAudit(null);
    setSectionNames([]);
    resetNavigation();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getProgressColor = (percentage, checklistStatus) => {
    if (checklistStatus === 'Completed') return 'success';
    if (percentage === 100) return 'success';
    if (percentage >= 60) return 'primary';
    if (percentage >= 30) return 'info';
    return 'warning';
  };

  const getFilteredChecklists = () => {
    if (activeTab === 'pending') return allChecklists.filter(checklist => checklist.status?.toLowerCase() === 'pending');
    if (activeTab === 'inProgress') return allChecklists.filter(checklist => checklist.status?.toLowerCase() === 'in progress');
    if (activeTab === 'completed') return allChecklists.filter(checklist => checklist.status?.toLowerCase() === 'completed');
    return allChecklists.filter(checklist => checklist.status?.toLowerCase() === 'pending');
  };

  const filteredChecklists = getFilteredChecklists();

  // Progress Indicator component (same as AuditSections)
  const ProgressIndicator = ({ progress }) => {
    if (!progress || progress.level !== 'section') return null;

    return (
      <div className="section-progress mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="text-muted text-uppercase small mb-0">Section Progress</h6>
          <span className="badge bg-primary">{progress.progressPercentage}%</span>
        </div>
        <div className="progress" style={{ height: '10px' }}>
          <div 
            className="progress-bar" 
            role="progressbar" 
            style={{ width: `${progress.progressPercentage}%` }}
            aria-valuenow={progress.progressPercentage} 
            aria-valuemin="0" 
            aria-valuemax="100"
          />
        </div>
        <div className="d-flex justify-content-between mt-2 small text-muted">
          <span>{progress.answeredQuestions} answered</span>
          <span>{progress.totalQuestions} total</span>
        </div>
      </div>
    );
  };

  // If an audit is selected, show the audit details view (exact same as AuditSections but view-only)
  if (selectedAudit) {
    return (
      <Container fluid className="py-4 view-only">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-start">
                <div className="d-flex align-items-center mb-2">
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="me-3"
                    onClick={backToAuditList}
                  >
                    <FaArrowLeft className="me-1" />
                    Back to List
                  </Button>
                
              </div>
              
            </div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="#" onClick={(e) => {
                    e.preventDefault();
                    resetNavigation();
                  }}>Sections</a>
                </li>
                
                {currentPath.map((pathItem, index) => (
                  <li 
                    key={index} 
                    className={`breadcrumb-item ${index === currentPath.length - 1 ? 'active' : ''}`}
                  >
                    {index === currentPath.length - 1 ? (
                      <span>{pathItem}</span>
                    ) : (
                      <a href="#" onClick={(e) => {
                        e.preventDefault();
                        navigateToPath(index);
                      }}>{pathItem}</a>
                    )}
                  </li>
                ))}
                
                {questions.length > 0 && currentPath.length > 0 && (
                  <li className="breadcrumb-item active">Questions</li>
                )}
              </ol>
            </nav>
          </Col>
        </Row>

        {sectionProgress && <ProgressIndicator progress={sectionProgress} />}

        {isSectionLoading ? (
          <div className="text-center py-5">
            <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-primary fw-bold mb-1">Loading Section Data</p>
            <p className="text-muted">Please wait...</p>
          </div>
        ) : (
          <>
            {/* Show sections if no section is selected */}
            {currentPath.length === 0 ? (
              <Row xs={1} md={2} lg={3} className="g-4">
                {sectionNames.map((sectionName, index) => {
                  const progress = sectionsProgress[sectionName] || {
                    progressPercentage: 0,
                    answeredQuestions: 0,
                    totalQuestions: 0
                  };

                  return (
                    <Col key={index}>
                      <Card 
                        className="h-100 shadow-sm hover-shadow"
                        onClick={() => handleSectionClick(sectionName, index)}
                        role="button"
                      >
                        <Card.Body className="d-flex flex-column">
                          <div className="text-center mb-3">
                            <CircularProgress 
                              percentage={progress.progressPercentage}
                              size={80}
                              showLabel={true}
                            />
                          </div>
                          <div className="text-center mb-3">
                            <h5 className="mb-1">{sectionName}</h5>
                            <small className="text-muted d-block">
                              {progress.totalQuestions} Questions
                            </small>
                          </div>
                          <div className="mt-auto">
                            <div className="d-flex justify-content-center gap-3 mb-2">
                              <div className="text-success">
                                <small>{progress.answeredQuestions} Answered</small>
                              </div>
                              <div className="text-danger">
                                <small>{progress.totalQuestions - progress.answeredQuestions} Remaining</small>
                              </div>
                            </div>
                            <div className="text-center">
                              <small className={`badge bg-${
                                progress.progressPercentage === 100 ? 'success' :
                                progress.progressPercentage > 0 ? 'warning' : 'danger'
                              }`}>
                                {progress.progressPercentage === 100 ? 'Completed' :
                                progress.progressPercentage > 0 ? 'In Progress' : 'Not Started'}
                              </small>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            ) : (
              <>
                {/* Show questions */}
                {questions.length > 0 && (
                  <QuestionsList
                    questions={questions}
                    answers={answers}
                    handleAnswerChange={() => {}} // Empty function for view-only
                    setAnswers={() => {}} // Empty function for view-only
                    selectedSection={currentPath[currentPath.length - 1]}
                    checklist={selectedAudit}
                    auditorId={selectedAudit.auditorId}
                    isViewOnly={true} // Always view-only for admin
                  />
                )}

                {/* Show subsections */}
                {subsections.length > 0 && !questions.length && (
                  <Row xs={1} md={2} lg={3} className="g-4">
                    {subsections.map((subsection, index) => {
                      const subsectionPath = [...currentPath, subsection.name].join('|');
                      const progress = subsectionProgress[subsectionPath] || {
                        progressPercentage: 0,
                        answeredQuestions: 0,
                        totalQuestions: 0
                      };

                      return (
                        <Col key={index}>
                          <Card 
                            className="h-100 shadow-sm hover-shadow"
                            onClick={() => handleItemClick(
                              subsection.name, 
                              currentLevel, 
                              currentPath
                            )}
                            role="button"
                          >
                            <Card.Body className="d-flex flex-column">
                              <div className="text-center mb-3">
                                <CircularProgress 
                                  percentage={progress.progressPercentage} 
                                  size={80}
                                  showLabel={true}
                                />
                              </div>
                              <div className="text-center mb-3">
                                <h5 className="mb-1">{subsection.name}</h5>
                                <small className="text-muted d-block">
                                  {progress.totalQuestions} Questions
                                </small>
                              </div>
                              <div className="mt-auto">
                                <div className="d-flex justify-content-center gap-3 mb-2">
                                  <div className="text-success">
                                    <small>{progress.answeredQuestions} Answered</small>
                                  </div>
                                  <div className="text-danger">
                                    <small>{progress.totalQuestions - progress.answeredQuestions} Remaining</small>
                                  </div>
                                </div>
                                <div className="text-center">
                                  <small className={`badge bg-${
                                    progress.progressPercentage === 100 ? 'success' :
                                    progress.progressPercentage > 0 ? 'warning' : 'danger'
                                  }`}>
                                    {progress.progressPercentage === 100 ? 'Completed' :
                                    progress.progressPercentage > 0 ? 'In Progress' : 'Not Started'}
                                  </small>
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                )}

                {/* No content found */}
                {!questions.length && !subsections.length && (
                  <div className="text-center py-5">
                    <i className="fas fa-folder-open fa-4x text-muted mb-3"></i>
                    <h4 className="text-muted">No content found</h4>
                    <Button 
                      variant="outline-primary" 
                      className="mt-3"
                      onClick={resetNavigation}
                    >
                      Return to Sections
                    </Button>
                  </div>
                )}

                {/* Navigation buttons */}
                {(questions.length > 0 || subsections.length > 0) && (
                  <div 
                    className="navigation-buttons d-flex justify-content-between mt-4"
                    style={{
                      position: 'fixed',
                      bottom: '1rem',
                      left: '1rem',
                      right: '1rem',
                      zIndex: 1000,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      padding: '1rem',
                      borderRadius: '0.5rem',
                      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <Button 
                      variant="outline-secondary"
                      onClick={() => {
                        if (currentPath.length === 1) {
                          resetNavigation();
                        } else {
                          navigateToPath(currentPath.length - 2);
                        }
                      }}
                    >
                      <FaArrowLeft className="me-2" />
                      Back
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Same styles as AuditSections */}
        <style jsx>{`
          .hover-shadow:hover {
            transform: translateY(-5px);
            cursor: pointer;
            transition: transform 0.2s ease-in-out;
          }
          .circular-progress {
            position: relative;
            margin-left: 1rem;
          }
          .progress-bg {
            transform: rotate(-90deg);
            transform-origin: 50% 50%;
          }
          .progress-bar {
            transform: rotate(-90deg);
            transform-origin: 50% 50%;
            transition: stroke-dashoffset 0.35s, stroke 0.35s;
          }
          .progress-text-large {
            font-size: 24px;
            font-weight: 700;
            transition: fill 0.35s;
          }
          .breadcrumb-item a {
            text-decoration: none;
            color: #0d6efd;
          }
          .breadcrumb-item a:hover {
            text-decoration: underline;
          }
          .navigation-buttons {
            position: sticky;
            bottom: 1rem;
            z-index: 100;
            padding: 1rem;
            background-color: rgba(255, 255, 255, 0.9);
            backdrop-filter: blur(8px);
            border-radius: 0.5rem;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          }

          /* View-only mode styles */
          .view-only input,
          .view-only textarea,
          .view-only select,
          .view-only .form-control {
            pointer-events: none;
            background-color: #f8f9fa !important;
            border-color: #dee2e6 !important;
            cursor: not-allowed;
          }

          .view-only .btn-primary,
          .view-only .btn-success,
          .view-only .btn-info {
            display: none;
          }

          .view-only .file-upload-area,
          .view-only .file-upload-button {
            pointer-events: none;
            opacity: 0.6;
            background-color: #f8f9fa !important;
          }

          .view-only .btn-outline-danger {
            display: none;
          }

          .view-only .form-check-input {
            pointer-events: none;
            background-color: #f8f9fa !important;
          }

          .view-only .form-range {
            pointer-events: none;
            opacity: 0.6;
          }

          .view-only .navigation-buttons .btn-outline-secondary {
            pointer-events: auto;
            opacity: 1;
            background-color: transparent !important;
          }

          .alert-info {
            border-left: 4px solid #0dcaf0;
            background-color: #cff4fc;
            border-color: #b8e6f0;
            color: #055160;
          }
        `}</style>
      </Container>
    );
  }

  // Original audit list view
  return (
    <Container fluid className="p-0">
      {/* Header */}
        <Container fluid className="px-4 py-4">
          <Row className="align-items-center">
            <Col>
              <h4 className="mb-1">
                <FaEye className="me-2" />
                Audit Overview
              </h4>
              <p className="mb-0 opacity-75">Monitor all audit checklists across different statuses</p>
            </Col>
          </Row>
        </Container>

      {/* Stats Cards */}
      <div className="px-4 py-4">
        {error && (
          <Alert variant="danger" className="mb-4">
            <Alert.Heading>Error Loading Checklists</Alert.Heading>
            <p className="mb-0">{error}</p>
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-primary fw-bold mb-1">Loading All Audits</p>
            <p className="text-muted">Please wait...</p>
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-0 pt-4 pb-0 px-4">
              {/* Custom Tab Navigation */}
              <div className="custom-tabs mb-3">
                <button
                  className={`custom-tab-button ${activeTab === 'pending' ? 'active' : ''}`}
                  onClick={() => handleTabSelect('pending')}
                >
                  <FaClock className="me-1" size={14} />
                  Plan ({stats.pending})
                </button>
                <button
                  className={`custom-tab-button ${activeTab === 'inProgress' ? 'active' : ''}`}
                  onClick={() => handleTabSelect('inProgress')}
                >
                  <FaHourglassHalf className="me-1" size={14} />
                  In Progress ({stats.inProgress})
                </button>
                <button
                  className={`custom-tab-button ${activeTab === 'completed' ? 'active' : ''}`}
                  onClick={() => handleTabSelect('completed')}
                >
                  <FaCheckCircle className="me-1" size={14} />
                  Completed ({stats.completed})
                </button>
              </div>
            </Card.Header>
            <Card.Body className="p-4">
              {filteredChecklists.length === 0 ? (
                <div className="text-center py-5">
                  <div className="empty-state-icon bg-light text-muted mb-3">
                    <FaEye size={32} />
                  </div>
                  <h5>No {activeTab !== 'pending' ? activeTab : ''} Audit Checklists Found</h5>
                  <p className="text-muted mb-4">
                    {activeTab === 'pending' 
                      ? 'No pending checklists are available at the moment.'
                      : `No ${activeTab} checklists are available at the moment.`}
                  </p>
                  {activeTab === 'pending' && (
                    <Link to="/ChecklistOverview" className="btn btn-primary">
                      Create New Checklist
                    </Link>
                  )}
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead>
                      <tr>
                        <th>Audit</th>
                        <th>Branch</th>
                        <th>Auditor</th>
                        <th>Start Date</th>
                        <th>Progress</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredChecklists.map((checklist) => (
                        <tr key={`${checklist.id}-${checklist.branchId}-${checklist.status}`}>
                          <td>
                            <div className="fw-medium">{checklist.checklistName}</div>
                          </td>
                          <td>
                            <div className="small text-muted">
                              {checklist.branchName || 'N/A'}
                            </div>
                          </td>
                         
                          <td>
                            <div className="d-flex align-items-center">
                              <FaUser className="text-muted me-2" size={12} />
                              {checklist.auditorName || 'Not assigned'}
                            </div>
                          </td>
                          <td>
                            <div className="small text-muted">
                              {formatDate(checklist.startDate || checklist.createdAt)}
                            </div>
                          </td>
                         
                          <td style={{ minWidth: '200px' }}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <small>Progress</small>
                              <Badge 
                                bg={getProgressColor(checklist.progressPercentage || 0, checklist.status)}
                              >
                                {checklist.status?.toLowerCase() === 'pending' ? 'Not Started' : `${Math.round(checklist.progressPercentage || 0)}%`}
                              </Badge>
                            </div>
                            <div className="progress" style={{ height: '8px' }}>
                              <div 
                                className={`progress-bar bg-${getProgressColor(checklist.progressPercentage || 0, checklist.status)}`} 
                                role="progressbar" 
                                style={{ width: `${checklist.progressPercentage || 0}%` }}
                                aria-valuenow={checklist.progressPercentage || 0} 
                                aria-valuemin="0" 
                                aria-valuemax="100">
                              </div>
                            </div>
                            <div className="small mt-1 text-muted">
                              {checklist.status?.toLowerCase() === 'pending' ? 
                                (checklist.totalQuestions ? `Not started - ${checklist.totalQuestions} questions` : 'Not started yet') :
                                (checklist.totalQuestions && checklist.totalQuestions > 0) ? 
                                  `${checklist.answeredQuestions || 0}/${checklist.totalQuestions} questions` : 
                                  'No data'
                              }
                            </div>
                          </td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleAuditClick(checklist)}
                              className="d-flex align-items-center"
                            >
                              <FaEye className="me-1" />
                              View Details
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        )}
      </div>

      <style jsx>{`
       
        .empty-state-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          margin: 0 auto;
        }

        .table th {
          font-weight: 600;
          color: #344767;
          border-bottom: 2px solid #e9ecef;
        }
        
        .progress {
          border-radius: 10px;
          background-color: #f0f2f5;
        }
        
        .progress-bar {
          border-radius: 10px;
        }

        .card {
          transition: transform 0.2s ease-in-out;
        }

        .card:hover {
          transform: translateY(-2px);
        }
        
        /* Custom Tab Styles */
        .custom-tabs {
          display: flex;
          border-bottom: 1px solid #dee2e6;
        }
        
        .custom-tab-button {
          font-weight: 600;
          color: #495057;
          border: none;
          background: transparent;
          border-bottom: 2px solid transparent;
          padding: 0.5rem 1rem;
          margin-right: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .custom-tab-button:hover {
          color: #764ba2;
          border-color: transparent;
        }
        
        .custom-tab-button.active {
          color: #764ba2;
          border-bottom: 2px solid #764ba2;
          background: transparent;
        }
      `}</style>
    </Container>
  );
}

export default IndividualAuditOverview;